import { Injectable, signal, computed, effect, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

/** The three persisted theme preference choices. */
export type ThemePreference = 'dark' | 'light' | 'system';

/** The resolved concrete theme (never "system"). */
export type ResolvedTheme = 'dark' | 'light';

const STORAGE_KEY = 'tb-theme-preference';
const DATA_ATTR   = 'data-theme';

/**
 * ThemeService — signal-based light/dark/system theme toggle (DESIGN.md §5, Wave 0A).
 *
 * - Reads preference from localStorage on construction; falls back to "system".
 * - Sets `data-theme="dark"|"light"` on `<html>` so CSS @mixin tokens-light/dark apply.
 * - `resolved` signal: the actual theme in effect (system → OS preference).
 * - Provided in root; inject anywhere.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly doc = inject(DOCUMENT);

  /** User's stored preference (dark | light | system). */
  readonly preference = signal<ThemePreference>(this._readStored());

  /** The concrete theme currently active. */
  readonly resolved = computed<ResolvedTheme>(() => {
    const pref = this.preference();
    if (pref !== 'system') return pref;
    return this._osPrefers();
  });

  constructor() {
    // Apply data-theme attribute whenever resolved changes.
    effect(() => {
      const theme = this.resolved();
      this.doc.documentElement.setAttribute(DATA_ATTR, theme);
      // Mirror color-scheme so scrollbars, inputs etc respect the theme.
      this.doc.documentElement.style.colorScheme = theme;
    });
  }

  /** Cycle: dark → light → system → dark */
  toggle(): void {
    const next: Record<ThemePreference, ThemePreference> = {
      dark: 'light',
      light: 'system',
      system: 'dark',
    };
    this.set(next[this.preference()]);
  }

  set(pref: ThemePreference): void {
    this.preference.set(pref);
    try {
      localStorage.setItem(STORAGE_KEY, pref);
    } catch {
      // Private-browsing / storage-quota — silently ignore.
    }
  }

  private _readStored(): ThemePreference {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === 'dark' || v === 'light' || v === 'system') return v;
    } catch { /* localStorage unavailable */ }
    return 'system';
  }

  private _osPrefers(): ResolvedTheme {
    try {
      return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    } catch {
      return 'dark';
    }
  }
}
